var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { getSignature, uploadToCloudinary } from '@/api/cloudinary';
import CropperModal from '@/components/CropperModal';
import { useApiContext } from '@/context/ApiContext';
/**
 * Компонент для множественной загрузки изображений с возможностью кропа для каждого
 */
export default function MultiUnifiedCloudinaryForm({ onUploaded, defaultFiles = [], cropOutputType = 'image/jpeg', cropQuality = 0.9, maxFileMB = 3, useProxyForUrlCrop = true, uploadPreset = 'avatars', }) {
    var _a;
    const { t } = useTranslation();
    const { useMockApi } = useApiContext();
    const [imageItems, setImageItems] = useState([]);
    const [cropOpen, setCropOpen] = useState(false);
    const [cropImageId, setCropImageId] = useState(null);
    const [cropSrc, setCropSrc] = useState('');
    const [revokeOnClose, setRevokeOnClose] = useState(false);
    const [uploadingAll, setUploadingAll] = useState(false);
    const [error, setError] = useState('');
    // Инициализация изображений из defaultFiles
    useEffect(() => {
        if (defaultFiles.length === 0) {
            // Очищаем существующие preview URLs при очистке defaultFiles
            setImageItems((prev) => {
                prev.forEach((item) => {
                    try {
                        URL.revokeObjectURL(item.previewSrc);
                    }
                    catch (_a) {
                        // ignore
                    }
                });
                return [];
            });
            return;
        }
        // Очищаем старые preview URLs
        setImageItems((prev) => {
            prev.forEach((item) => {
                try {
                    URL.revokeObjectURL(item.previewSrc);
                }
                catch (_a) {
                    // ignore
                }
            });
            return [];
        });
        const newItems = defaultFiles.map((file) => {
            const id = `${Date.now()}-${Math.random()}`;
            const previewSrc = URL.createObjectURL(file);
            return {
                id,
                file,
                previewSrc,
                croppedFile: null,
                uploading: false,
                progress: 0,
                error: null,
                result: null,
            };
        });
        setImageItems(newItems);
        // Cleanup при размонтировании или изменении defaultFiles
        return () => {
            newItems.forEach((item) => {
                try {
                    URL.revokeObjectURL(item.previewSrc);
                }
                catch (_a) {
                    // ignore
                }
            });
        };
    }, [defaultFiles]);
    const openCropForImage = (imageId) => {
        const item = imageItems.find((i) => i.id === imageId);
        if (!item)
            return;
        setError('');
        const blobUrl = URL.createObjectURL(item.croppedFile || item.file);
        setCropSrc(blobUrl);
        setCropImageId(imageId);
        setRevokeOnClose(true);
        setCropOpen(true);
    };
    const closeCrop = () => {
        if (revokeOnClose && cropSrc) {
            try {
                URL.revokeObjectURL(cropSrc);
            }
            catch (_a) {
                console.warn('Error on revoke URL:', cropSrc);
            }
        }
        setCropOpen(false);
        setCropSrc('');
        setCropImageId(null);
        setRevokeOnClose(false);
    };
    const onCropped = (imageId, croppedFile) => {
        setImageItems((prev) => prev.map((item) => {
            if (item.id === imageId) {
                // Освобождаем старый preview если был croppedFile
                if (item.croppedFile) {
                    try {
                        URL.revokeObjectURL(item.previewSrc);
                    }
                    catch (_a) {
                        // ignore
                    }
                }
                const newPreviewSrc = URL.createObjectURL(croppedFile);
                return Object.assign(Object.assign({}, item), { croppedFile, previewSrc: newPreviewSrc, error: null });
            }
            return item;
        }));
    };
    const removeImage = (imageId) => {
        setImageItems((prev) => {
            const item = prev.find((i) => i.id === imageId);
            if (item) {
                try {
                    URL.revokeObjectURL(item.previewSrc);
                }
                catch (_a) {
                    // ignore
                }
            }
            return prev.filter((i) => i.id !== imageId);
        });
    };
    const ensureSize = (f) => {
        const mb = f.size / (1024 * 1024);
        if (mb > maxFileMB)
            throw new Error(`File is too large: ${mb.toFixed(2)} MB (limit ${maxFileMB} MB)`);
    };
    const uploadSingleImage = (item) => __awaiter(this, void 0, void 0, function* () {
        const fileToUpload = item.croppedFile || item.file;
        ensureSize(fileToUpload);
        if (useMockApi) {
            return {
                asset_folder: 'mock_folder',
                asset_id: 'mock_id',
                bytes: 0,
                created_at: new Date().toISOString(),
                display_name: 'mock_id',
                format: 'jpg',
                height: 0,
                original_filename: fileToUpload.name,
                public_id: 'mock_id',
                secure_url: item.previewSrc,
                url: item.previewSrc,
                width: 0,
            };
        }
        const sig = yield getSignature(uploadPreset);
        const data = yield uploadToCloudinary(fileToUpload, sig, (evt) => {
            if (!evt.total)
                return;
            const progress = Math.round((evt.loaded * 100) / evt.total);
            setImageItems((prev) => prev.map((i) => (i.id === item.id ? Object.assign(Object.assign({}, i), { progress }) : i)));
        });
        return data;
    });
    const uploadAll = () => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        if (imageItems.length === 0) {
            setError(t('upload.images.emptyInput'));
            return;
        }
        setError('');
        setUploadingAll(true);
        // Обновляем состояние для всех элементов
        setImageItems((prev) => prev.map((item) => (Object.assign(Object.assign({}, item), { uploading: true, progress: 0, error: null }))));
        try {
            const results = [];
            // Загружаем изображения последовательно
            for (const item of imageItems) {
                try {
                    const result = yield uploadSingleImage(item);
                    results.push(result);
                    setImageItems((prev) => prev.map((i) => i.id === item.id
                        ? Object.assign(Object.assign({}, i), { uploading: false, progress: 100, result }) : i));
                }
                catch (err) {
                    const message = typeof err === 'object' && err !== null
                        ? // @ts-expect-error safe optional chaining for possible axios error shape
                            ((_c = (_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error) === null || _c === void 0 ? void 0 : _c.message) || err.message
                        : '';
                    const errorMessage = message || t('upload.error');
                    setImageItems((prev) => prev.map((i) => i.id === item.id
                        ? Object.assign(Object.assign({}, i), { uploading: false, error: errorMessage }) : i));
                }
            }
            // Если все загрузились успешно, вызываем callback
            if (results.length > 0 && onUploaded) {
                onUploaded(results);
            }
        }
        catch (err) {
            console.error(err);
            setError(t('upload.error'));
        }
        finally {
            setUploadingAll(false);
        }
    });
    const handleCancel = () => {
        // Освобождаем все preview URLs
        imageItems.forEach((item) => {
            try {
                URL.revokeObjectURL(item.previewSrc);
            }
            catch (_a) {
                // ignore
            }
        });
        if (onUploaded) {
            onUploaded(null);
        }
    };
    const allUploaded = imageItems.length > 0 && imageItems.every((item) => item.result !== null);
    const canUpload = imageItems.length > 0 && !uploadingAll && !allUploaded;
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "upload-image-form", style: styles.form, children: [_jsxs("div", { style: styles.header, children: [_jsxs("div", { style: styles.headerTitle, children: [t('upload.images.imageFile'), " (", imageItems.length, ")"] }), _jsx("button", { type: "button", onClick: handleCancel, style: styles.closeButton, "aria-label": t('common.close'), children: "\u00D7" })] }), _jsx("div", { style: styles.imagesList, children: imageItems.map((item) => (_jsxs("div", { style: styles.imageItem, children: [_jsxs("div", { style: styles.imagePreview, children: [_jsx("img", { src: item.previewSrc, alt: "preview", style: styles.previewImg }), item.croppedFile && (_jsx("div", { style: styles.croppedBadge, children: t('upload.images.crop') }))] }), _jsxs("div", { style: styles.imageControls, children: [_jsx("div", { style: styles.imageName, children: item.file.name }), _jsxs("div", { style: styles.imageActions, children: [_jsx("button", { type: "button", onClick: () => openCropForImage(item.id), style: styles.button, disabled: item.uploading || !!item.result, children: t('upload.images.crop') }), _jsx("button", { type: "button", onClick: () => removeImage(item.id), style: styles.removeButton, disabled: item.uploading || uploadingAll, children: t('common.delete') })] }), item.uploading && (_jsxs("div", { style: styles.progressContainer, children: [_jsx("div", { style: styles.progressOuter, children: _jsx("div", { style: Object.assign(Object.assign({}, styles.progressInner), { width: `${item.progress}%` }) }) }), _jsxs("div", { style: styles.progressText, children: [item.progress, "%"] })] })), item.error && _jsx("div", { style: styles.errorText, children: item.error }), item.result && _jsx("div", { style: styles.successMessage, children: t('upload.success') })] })] }, item.id))) }), imageItems.length === 0 && (_jsx("div", { style: styles.emptyMessage, children: t('upload.images.emptyInput') })), _jsxs("div", { style: styles.buttonsContainer, children: [uploadingAll || allUploaded ? (_jsxs("div", { style: styles.statusContainer, children: [uploadingAll && (_jsxs("div", { style: styles.statusText, children: [t('upload.uploading'), "... (", imageItems.filter((i) => i.result).length, "/", imageItems.length, ")"] })), allUploaded && (_jsxs("div", { style: styles.successAllMessage, children: [t('upload.success'), " - ", imageItems.length, ' ', imageItems.length === 1 ? 'изображение' : 'изображений', " \u0437\u0430\u0433\u0440\u0443\u0436\u0435\u043D\u043E"] }))] })) : (_jsxs(_Fragment, { children: [_jsxs("button", { type: "button", onClick: uploadAll, disabled: !canUpload, style: styles.primaryBtn, children: [t('upload.upload'), " \u0432\u0441\u0435 (", imageItems.length, ")"] }), onUploaded && (_jsx("button", { className: "secondary", style: styles.secondaryBtn, type: "button", onClick: handleCancel, children: t('common.cancel') }))] })), error && _jsx("div", { style: styles.errorMessage, children: error })] })] }), cropOpen &&
                cropImageId &&
                createPortal(_jsx(CropperModal, { open: cropOpen, onClose: closeCrop, src: cropSrc, outputType: cropOutputType, quality: cropQuality, outName: ((_a = imageItems.find((i) => i.id === cropImageId)) === null || _a === void 0 ? void 0 : _a.file.name) || 'image', onCropped: (f) => {
                        onCropped(cropImageId, f);
                        closeCrop();
                    } }), document.body)] }));
}
const styles = {
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '18px 10px',
        borderRadius: 8,
        border: '1px solid #dce1e6ff',
        background: '#f1f1f1ff',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.07)',
        maxWidth: 800,
        maxHeight: '90vh',
        overflow: 'auto',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
    },
    headerTitle: {
        fontWeight: 600,
        fontSize: '1.1rem',
        color: '#444',
    },
    closeButton: {
        border: 'none',
        background: 'transparent',
        fontSize: 24,
        cursor: 'pointer',
        color: '#666',
        lineHeight: 1,
        padding: 0,
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    imagesList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        maxHeight: '50vh',
        overflowY: 'auto',
        padding: '8px 0',
    },
    imageItem: {
        display: 'flex',
        gap: 12,
        padding: 12,
        borderRadius: 8,
        border: '1px solid #ddd',
        background: '#fff',
    },
    imagePreview: {
        position: 'relative',
        flexShrink: 0,
        width: 120,
        height: 120,
        borderRadius: 8,
        overflow: 'hidden',
        border: '1px solid #eee',
    },
    previewImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    croppedBadge: {
        position: 'absolute',
        top: 4,
        right: 4,
        background: '#4caf50',
        color: '#fff',
        fontSize: 10,
        padding: '2px 6px',
        borderRadius: 4,
        fontWeight: 600,
    },
    imageControls: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minWidth: 0,
    },
    imageName: {
        fontSize: '0.9rem',
        fontWeight: 500,
        color: '#333',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    imageActions: {
        display: 'flex',
        gap: 8,
        alignItems: 'center',
    },
    button: {
        flexShrink: 0,
        padding: '6px 12px',
        borderRadius: 8,
        border: '1px solid #888',
        background: '#fff',
        color: '#333',
        cursor: 'pointer',
        fontSize: '0.85rem',
    },
    removeButton: {
        flexShrink: 0,
        padding: '6px 12px',
        borderRadius: 8,
        border: '1px solid #dc3545',
        background: '#fff',
        color: '#dc3545',
        cursor: 'pointer',
        fontSize: '0.85rem',
    },
    progressContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    progressOuter: {
        flex: 1,
        height: 8,
        background: '#e0e0e0',
        borderRadius: 4,
        overflow: 'hidden',
    },
    progressInner: {
        height: 8,
        background: '#4a8',
        borderRadius: 4,
        transition: 'width .2s',
    },
    progressText: {
        fontSize: '0.75rem',
        color: '#666',
        minWidth: 40,
        textAlign: 'right',
    },
    errorText: {
        fontSize: '0.75rem',
        color: 'crimson',
    },
    successMessage: {
        fontSize: '0.75rem',
        color: '#4caf50',
        fontWeight: 500,
    },
    buttonsContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 8,
        flexWrap: 'wrap',
    },
    primaryBtn: {
        padding: '8px 20px',
        borderRadius: 8,
        border: '1px solid #1a73e8',
        background: '#1a73e8',
        color: '#fff',
        cursor: 'pointer',
        fontSize: '0.9rem',
        fontWeight: 500,
    },
    secondaryBtn: {
        padding: '8px 16px',
        borderRadius: 8,
        border: '1px solid #aaa',
        background: '#fff',
        color: '#333',
        cursor: 'pointer',
        fontSize: '0.9rem',
    },
    statusContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    statusText: {
        fontSize: '0.9rem',
        color: '#666',
    },
    successAllMessage: {
        fontSize: '0.9rem',
        color: '#4caf50',
        fontWeight: 500,
    },
    errorMessage: {
        color: 'crimson',
        fontSize: '0.85rem',
        marginLeft: 'auto',
    },
    emptyMessage: {
        textAlign: 'center',
        color: '#999',
        padding: '20px 0',
        fontSize: '0.9rem',
    },
};
